import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';

interface Screenshot {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  image: string;
}

const screenshots: Screenshot[] = [
  {
    id: 1,
    title: 'Smart Appointment Scheduling',
    subtitle: 'Never Miss Another Appointment',
    description: 'Transform your scheduling chaos into organized efficiency with our intuitive appointment management system. Say goodbye to double bookings, forgotten appointments, and unhappy customers. Our smart scheduling system helps you stay organized, saving you hours each week and keeping your customers happy with reliable, on-time service.',
    features: [
      'Automated SMS and email notifications to keep the customer up to date',
      'Quickly create and send invoices from appointments, saving you time in getting paid',
      'Automatic conflict detection preventing double bookings',
      'Easy viewing of which jobs employees are assigned to',
      'Easy tracking of job status',
      'Automatic asking for reviews',
      'Easily create recurring appointments so you can get your clients on service plans'
    ],
    image: '/images/screenshots/appointments.png'
  },
  {
    id: 2,
    title: 'Interactive Job Mapping',
    subtitle: 'See Your Entire Operation at a Glance',
    description: 'Visualize all your scheduled jobs on an interactive map that helps you understand your service area better. View customer locations, identify clusters of appointments, and help your teams navigate efficiently. The map view gives you a bird\'s eye perspective of your daily operations, making it easier to plan routes manually and ensure no area is overlooked.',
    features: [
      'Interactive map showing all scheduled appointments',
      'Color coded by day so you can know when you\'ll be in a certain area',
      'Visual map shows entire operation at a glance'
    ],
    image: '/images/screenshots/appointments-map.png'
  },
  {
    id: 3,
    title: 'Effortless Employee Management',
    subtitle: 'Your Team, Perfectly Coordinated',
    description: 'Managing a cleaning team shouldn\'t feel like solving a complex puzzle every day. Our employee scheduling system makes it simple to assign the right people to the right jobs, track their hours, and ensure nothing falls through the cracks. Reduce scheduling conflicts by 90% and give your team the clarity they need to deliver exceptional service every time.',
    features: [
      'Visual team availability at a glance',
      'Track employee schedules and assignments easily',
      'Performance tracking helps you recognize top performers'
    ],
    image: '/images/screenshots/employees-schedule.png'
  },
  {
    id: 4,
    title: 'Comprehensive Project Management',
    subtitle: 'Track Complex Jobs from Start to Finish',
    description: 'Manage large-scale projects with confidence using our powerful project management system. Track budgets, manage timelines, coordinate team members, and monitor progress all in one place. Perfect for renovation projects, long-term contracts, and multi-phase jobs. Keep your projects on track, on budget, and your clients informed every step of the way.',
    features: [
      'Track project budgets and expenses in real-time to prevent cost overruns',
      'Assign team members and monitor their progress on tasks',
      'Set milestones and deadlines to keep projects on schedule',
      'Link invoices, estimates, and orders to specific projects',
      'Monitor project progress with task completion tracking',
      'Store project documents and files in one organized location',
      'Generate detailed project reports for clients and stakeholders'
    ],
    image: '/images/screenshots/orders.png'
  },
];

const Screenshots: React.FC = () => {
  return (
    <section id="screenshots" className="screenshots-section-modern">
      <Container>
        <Row className="mb-5">
          <Col lg={8} className="mx-auto text-center">
            <h2 className="section-title mb-3">
              Powerful features that drive results
            </h2>
            <p className="section-subtitle">
              See how successful service businesses transform their operations
            </p>
          </Col>
        </Row>

        {screenshots.map((screenshot, index) => (
          <Row
            key={screenshot.id}
            className={`screenshot-row align-items-center ${index % 2 === 0 ? '' : 'flex-row-reverse'}`}
          >
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="screenshot-content-modern">
                <h3 className="screenshot-title-modern">
                  {screenshot.title}
                </h3>
                <p className="screenshot-description-modern">
                  {screenshot.description}
                </p>
                <ul className="feature-list-modern">
                  {screenshot.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            </Col>

            <Col lg={6}>
              <div className="screenshot-image-modern">
                <img
                  src={screenshot.image}
                  alt={screenshot.title}
                  className="w-100"
                />
              </div>
            </Col>
          </Row>
        ))}
      </Container>
    </section>
  );
};

export default Screenshots;